import {useState} from "react";
import {toast} from "sonner";
import {logger} from "../../../../utils/logger.ts";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {BookOpen, Calendar, FileText, Loader2, Video} from "lucide-react";
import {Button} from "../../../basic/button.tsx";
import {LiveChatSection} from "./LiveChatSection.tsx";
import {ContactInformation} from "./ContactInformation.tsx";
import {QuickLinks} from "./QuickLinks.tsx";
import {SupportResources} from "./SupportResources.tsx";
import {useGetCurrentUserQuery, useGetPortalTeamQuery} from "../../../../store/generated/api";
import {getVelasolarisUrl} from "../../../../utils/websiteUrlUtils";

interface QuickLink {
    title: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    action: () => void;
}

export function SupportView() {
    const {t, i18n} = useTranslationWithKeys();
    const [copiedTeamId, setCopiedTeamId] = useState(false);
    const [copiedUserId, setCopiedUserId] = useState(false);

    // Fetch real team and user data from API
    const {data: teamData, isLoading: isLoadingTeam, error: teamError} = useGetPortalTeamQuery({page: 0, size: 1});
    const {data: userData, isLoading: isLoadingUser, error: userError} = useGetCurrentUserQuery();

    // Extract team ID and user email from API responses
    const teamId = teamData?.team?.id;
    const userEmail = userData?.user?.email;
    const isLoading = isLoadingTeam || isLoadingUser;
    const hasError = teamError || userError;

    const handleCopyTeamId = () => {
        navigator.clipboard.writeText(teamId);
        setCopiedTeamId(true);
        toast.success(t('support.teamIdCopied'));
        setTimeout(() => setCopiedTeamId(false), 2000);
    };

    const handleCopyUserId = () => {
        navigator.clipboard.writeText(userEmail);
        setCopiedUserId(true);
        toast.success(t('support.userIdCopied'));
        setTimeout(() => setCopiedUserId(false), 2000);
    };

    const handleStartChat = () => {
        if ((window as any).HubSpotConversations) {
            try {
                (window as any).HubSpotConversations.widget.open();
                logger.log("HubSpot chat widget opened");
            } catch (error) {
                logger.error("Failed to open HubSpot chat widget:", error);
                toast.error(t('support.chatLoadError'));
            }
        } else {
            toast.error(t('support.chatLoadError'));
            logger.error("HubSpot conversations widget not available - possibly blocked by ad blocker");
        }
    };

    const quickLinks: QuickLink[] = [
        {
            title: t('support.faq'),
            description: t('support.faqDescription'),
            icon: FileText,
            action: () => window.open(getVelasolarisUrl('faq/', i18n.language), '_blank')
        },
        {
            title: t('support.userManual'),
            description: t('support.userManualDescription'),
            icon: BookOpen,
            action: () => window.open(getVelasolarisUrl('user-manual/', i18n.language), '_blank')
        },
        {
            title: t('support.videoTutorials'),
            description: t('support.videoTutorialsDescription'),
            icon: Video,
            action: () => window.open(getVelasolarisUrl('support/video/', i18n.language), '_blank')
        },
        {
            title: t('support.trainings'),
            description: t('support.trainingsDescription'),
            icon: Calendar,
            action: () => window.open(getVelasolarisUrl('webinare/', i18n.language), '_blank')
        },
        {
            title: t('support.releaseNotes'),
            description: t('support.releaseNotesDescription'),
            icon: FileText,
            action: () => window.open(getVelasolarisUrl('release-notes/', i18n.language), '_blank')
        },
    ];

    if (isLoading) {
        return (
            <div className="container mx-auto py-8 px-4 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-500"/>
            </div>
        );
    }

    if (hasError) {
        return (
            <div className="container mx-auto py-8 px-4">
                <div className="text-center py-12">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {t('common.errorLoadingData')}
                    </p>
                    <Button variant="outline" onClick={() => window.location.reload()}>
                        {t('common.retry')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h1>{t('support.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('support.subtitle')}</p>
            </div>

            <LiveChatSection
                onStartChat={handleStartChat}
            />

            <div className="grid gap-6 lg:grid-cols-2">
                <ContactInformation
                    teamId={teamId}
                    userId={userEmail}
                    copiedTeamId={copiedTeamId}
                    copiedUserId={copiedUserId}
                    onCopyTeamId={handleCopyTeamId}
                    onCopyUserId={handleCopyUserId}
                />

                <QuickLinks quickLinks={quickLinks}/>
            </div>

            <SupportResources/>
        </div>
    );
}